import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
export const Button = ({
  children,
  onClick,
  className = '',
  variant = 'primary',
  size = 'lg',
  href
}: {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  variant?: 'primary' | 'accent' | 'outline';
  size?: 'md' | 'lg' | 'xl';
  href?: string;
}) => {
  const baseStyles = 'inline-flex items-center justify-center font-bold rounded-xl transition-all duration-300 transform active:scale-95 text-center';
  const variants = {
    primary: 'bg-[#00F5A0] hover:bg-[#00d98c] text-[#061528] shadow-[0_0_20px_rgba(0,245,160,0.35)] hover:shadow-[0_0_30px_rgba(0,245,160,0.55)]',
    accent: 'bg-accent hover:bg-accent-hover text-[#061528] shadow-[0_0_20px_rgba(45,212,191,0.3)]',
    outline: 'bg-transparent border-2 border-primary text-primary hover:bg-primary/10'
  };
  const sizes = {
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg w-full sm:w-auto',
    xl: 'px-10 py-5 text-xl w-full'
  };
  const classes = `${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`;
  if (href) {
    return <a href={href} className={`${classes} text-[24px]`} onClick={e => {
      if (onClick) onClick();
    }} data-id="element-330">
        {children}
      </a>;
  }
  return <button onClick={onClick} className={classes} data-id="element-331">
      {children}
    </button>;
};
export const Countdown = ({
  initialHours = 23,
  initialMinutes = 24,
  initialSeconds = 40
}) => {
  const [timeLeft, setTimeLeft] = useState({
    hours: initialHours,
    minutes: initialMinutes,
    seconds: initialSeconds
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return {
            ...prev,
            seconds: prev.seconds - 1
          };
        } else if (prev.minutes > 0) {
          return {
            ...prev,
            minutes: prev.minutes - 1,
            seconds: 59
          };
        } else if (prev.hours > 0) {
          return {
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const format = (num: number) => num.toString().padStart(2, '0');
  return <div className="flex items-center justify-center gap-2 text-3xl md:text-5xl font-black text-accent font-mono tracking-wider" data-id="element-332">
      <div className="bg-dark-card border border-dark-border px-4 py-2 rounded-lg shadow-inner" data-id="element-333">
        {format(timeLeft.hours)}
        <span className="text-sm md:text-xl text-cyan-200/60 ml-1" data-id="element-334">hs</span>
      </div>
      <span className="text-dark-border" data-id="element-335">:</span>
      <div className="bg-dark-card border border-dark-border px-4 py-2 rounded-lg shadow-inner" data-id="element-336">
        {format(timeLeft.minutes)}
        <span className="text-sm md:text-xl text-cyan-200/60 ml-1" data-id="element-337">min</span>
      </div>
      <span className="text-dark-border" data-id="element-338">:</span>
      <div className="bg-dark-card border border-dark-border px-4 py-2 rounded-lg shadow-inner" data-id="element-339">
        {format(timeLeft.seconds)}
        <span className="text-sm md:text-xl text-cyan-200/60 ml-1" data-id="element-340">seg</span>
      </div>
    </div>;
};
const fakeBuyers = ['Sebastián M. de Buenos Aires compró SUPERKIT IA + El Arquitecto', 'Lorena P. de Córdoba compró SUPERKIT IA', 'Rodrigo V. de Rosario compró SUPERKIT IA + El Arquitecto', 'Marta C. de Mendoza compró SUPERKIT IA', 'Damián R. de La Plata compró SUPERKIT IA + El Arquitecto'];
export const ToastNotification = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [buyerIndex, setBuyerIndex] = useState(0);
  useEffect(() => {
    const showInterval = setInterval(() => {
      setBuyerIndex(prev => (prev + 1) % fakeBuyers.length);
      setIsVisible(true);
      setTimeout(() => {
        setIsVisible(false);
      }, 5000);
    }, 20000);
    const initialTimeout = setTimeout(() => {
      setIsVisible(true);
      setTimeout(() => setIsVisible(false), 5000);
    }, 5000);
    return () => {
      clearInterval(showInterval);
      clearTimeout(initialTimeout);
    };
  }, []);
  return <AnimatePresence data-id="element-341">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: 50,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 50,
      x: -20
    }} className="fixed bottom-4 left-4 z-50 max-w-sm bg-dark-card border border-primary/30 rounded-lg shadow-2xl p-4 flex items-start gap-3" data-id="element-342">
          <div className="bg-primary/20 p-2 rounded-full mt-1" data-id="element-343">
            <div className="w-2 h-2 bg-primary rounded-full animate-pulse" data-id="element-344"></div>
          </div>
          <div className="flex-1" data-id="element-345">
            <p className="text-xs text-cyan-200/70 font-semibold mb-1" data-id="element-346">
              ✓ Compra verificada
            </p>
            <p className="text-sm text-white" data-id="element-347">{fakeBuyers[buyerIndex]}</p>
          </div>
          <button onClick={() => setIsVisible(false)} className="text-cyan-200/50 hover:text-white" data-id="element-348">
            <XIcon size={16} data-id="element-349" />
          </button>
        </motion.div>}
    </AnimatePresence>;
};
export const SectionWrapper = ({
  children,
  className = '',
  id = ''
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) => <motion.section id={id} initial={{
  opacity: 0,
  y: 40
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.6
}} className={`py-16 md:py-24 px-4 max-w-6xl mx-auto ${className}`} data-id="element-350">
    {children}
  </motion.section>;